import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import debounce from 'lodash.debounce'

import splash from '../components/splash'

gsap.registerPlugin(ScrollTrigger)

// Mobile browser chrome (address bar) show/hide changes innerHeight and would
// otherwise fire spurious refreshes that make pinned triggers jump.
ScrollTrigger.config({ ignoreMobileResize: true })

function init() {
    splash()
    scrollAnimation()
    initCoastalSophistication()
    initLocationMap()

    // Refresh after all assets (images, fonts) load so trigger positions are accurate
    window.addEventListener('load', () => ScrollTrigger.refresh())
}

function scrollAnimation() {
    const introduction = document.getElementById('introduction')
    if (!introduction) return

    const scrollIndicator = document.getElementById('scroll-indicator')
    if (!scrollIndicator) return

    const videoClip = document.querySelector('[data-video-clip]')
    if (!videoClip) return

    const introTl = gsap.timeline()
    introTl.to(scrollIndicator, {
        yPercent: -100,
        ease: 'power1.inOut',
        duration: 1,
    })

    ScrollTrigger.create({
        trigger: introduction,
        start: 'top top',
        end: 'bottom top',
        pin: true,
        scrub: 1,
        anticipatePin: 1,
        // Topmost pinned trigger — refresh first so spacers below resolve in order.
        refreshPriority: 2,
        invalidateOnRefresh: true,
        animation: introTl,
        onEnter: () => {
            videoClip.classList.add('unclipped')
        },
        onLeaveBack: () => {
            videoClip.classList.remove('unclipped')
        },
    })
}

function initCoastalSophistication() {
    const section = document.querySelector('[data-coastal]')
    if (!section) return

    const box = section.querySelector('[data-coastal-box]')
    if (!box) return

    // The background is "pinned" via native CSS position: sticky (smooth, no
    // spacers). GSAP only transforms the box on scrub — no ScrollTrigger pin,
    // which is what made it jumpy.
    const tl = gsap.timeline({
        scrollTrigger: {
            trigger: section,
            start: 'top top',
            end: 'bottom bottom',
            scrub: true,
            invalidateOnRefresh: true,
        },
    })

    // 1. Hold: box fully below the viewport, only the background is visible.
    // 2. Rise: box scrolls up into its centered position, finishing right as the
    //    section releases — so there's no dead scroll after it settles.
    tl.fromTo(box, { y: () => window.innerHeight }, { y: 0, ease: 'none', duration: 0.7 }, 0.3)
}

let st
let mapClickController = null

function initLocationMap() {
    const locationMap = document.querySelector('[data-location-map]')
    if (!locationMap) return

    const tabs = locationMap.querySelector('[data-location-map-tabs]')
    if (!tabs) return

    function getValues() {
        const locationMapHeight = locationMap.getBoundingClientRect().height
        const tabsHeight = tabs.getBoundingClientRect().height
        const style = window.getComputedStyle(tabs)
        const margin = parseFloat(style.getPropertyValue('margin-top'))
        const padding = parseFloat(style.getPropertyValue('padding-top'))

        return { locationMapHeight, tabsHeight, margin, padding }
    }

    st = ScrollTrigger.create({
        trigger: tabs,
        start: () => {
            const { tabsHeight, margin } = getValues()
            return `top ${window.innerHeight - tabsHeight - margin}px`
        },
        end: () => {
            const { locationMapHeight, tabsHeight, margin, padding } = getValues()
            const extra = window.innerWidth < 768 ? tabsHeight + margin + padding : 0
            return `bottom ${window.innerHeight - locationMapHeight + tabsHeight + margin - extra}px`
        },
        invalidateOnRefresh: true,
        anticipatePin: 1,
        // Lower on the page than the coastal section; refresh after it.
        refreshPriority: 0,
        pin: true,
    })

    const tabsBtns = tabs.querySelectorAll('button')
    if (!tabsBtns) return

    const pinLocations = locationMap.querySelectorAll('.pin-location')
    if (!pinLocations) return

    if (mapClickController) mapClickController.abort()
    mapClickController = new AbortController()

    tabsBtns.forEach((btn, i) => {
        btn.addEventListener(
            'click',
            () => {
                tabsBtns.forEach((btn) => btn.classList.remove('active'))
                btn.classList.add('active')

                pinLocations.forEach((pinLocation, j) => {
                    if (j === i) {
                        pinLocation.classList.add('active')
                    } else {
                        pinLocation.classList.remove('active')
                    }
                })
            },
            { signal: mapClickController.signal }
        )
    })
}

window.addEventListener(
    'resize',
    debounce(() => {
        if (st) st.kill()

        initLocationMap()
    }, 300)
)

export default init
